import React, { useState, useEffect, useCallback } from 'react'
import Button from '@/components/ui/Button'
import Tag from '@/components/ui/Tag'
import SidePanel from '@/components/ui/SidePanel'
import MemoModal from '@/components/ui/MemoModal'
import RequiredLabel from '@/components/ui/RequiredLabel'
import { designTokens } from '@/utils/design-tokens'
import { handleApiError, ErrorMessages } from '@/utils/api-error-handler'
import { useAlert } from '@/components/ui/Alert/exports'
import { useAppSelector } from '@/store/hooks'
import { useCanUseAI } from '@/hooks/useCanUseAI'
import DepartmentSelect from '@/components/ui/DepartmentSelect'
import SearchableMemberSelect from './SearchableMemberSelect'
import BudgetSection from './BudgetSection'
import PerformanceSection from './PerformanceSection'
import BusinessLabelSelector from './BusinessLabelSelector'
import BusinessDetailFields from './BusinessDetailFields'
import BusinessExtraFields from './BusinessExtraFields'
import { TaskService } from '@/api/generated/services/TaskService'
import { TaskBudgetService } from '@/api/generated/services/TaskBudgetService'
import { TaskPerformanceService } from '@/api/generated/services/TaskPerformanceService'
import { OrgMemberService } from '@/api/generated/services/OrgMemberService'
import { DepartmentService } from '@/api/generated/services/DepartmentService'
import { BusinessService } from '@/api/generated/services/BusinessService'
import type { TaskFullCreateRequest } from '@/api/generated/models/TaskFullCreateRequest'
import type { TaskUpdateRequest } from '@/api/generated/models/TaskUpdateRequest'
import type { TaskDetailInfoCreateRequest } from '@/api/generated/models/TaskDetailInfoCreateRequest'
import type { TaskExtraInfoCreateRequest } from '@/api/generated/models/TaskExtraInfoCreateRequest'
import type { TaskBudgetCreateRequest } from '@/api/generated/models/TaskBudgetCreateRequest'
import type { TaskBudgetUpdateRequest } from '@/api/generated/models/TaskBudgetUpdateRequest'
import type { TaskBudgetItemsBulkUpdateRequest } from '@/api/generated/models/TaskBudgetItemsBulkUpdateRequest'
import type { TaskPerformanceCreateRequest } from '@/api/generated/models/TaskPerformanceCreateRequest'
import type { TaskPerformanceUpdateRequest } from '@/api/generated/models/TaskPerformanceUpdateRequest'
import type { TaskPerformanceItemsBulkUpdateRequest } from '@/api/generated/models/TaskPerformanceItemsBulkUpdateRequest'
import type { OrgMemberResponse } from '@/api/generated/models/OrgMemberResponse'
import type { DepartmentResponse } from '@/api/generated/models/DepartmentResponse'
import type { PaginatedResponse } from '@/api/generated/models/PaginatedResponse'
import { BusinessResponse, TaskBudgetItemService, TaskPerformanceItemService } from '@/api'

interface TaskCreatePanelProps {
  isOpen: boolean
  onClose: () => void
  taskSubgroupId: string
  taskSubgroupName: string
  taskGroupName: string
  businessName: string
  businessId: string
  onSuccess?: (createdId?: string) => void
  // 수정 모드 관련 props
  editMode?: boolean
  taskId?: string
  initialTaskData?: {
    name?: string
    assigneeId?: string
    assigneeName?: string
    subAssigneeId?: string
    subAssigneeName?: string
    managerId?: string
    managerName?: string
    reviewerId?: string
    reviewerName?: string
    departmentId?: string
    departmentName?: string
    startAt?: string
    endAt?: string
    description?: string
    budget?: {
      id: string
      resultDescription: string
      items: Array<{
        id: string
        name: string
        targetFigure: number
        doneFigure: number
      }>
    }
    performance?: {
      id: string
      name: string
      unit: string
      items: Array<{
        id: string
        name: string
        targetFigure: number
        doneFigure: number
      }>
    }
    labels?: Array<{ id: string; key: string }>
    detailInfos?: Array<{ id: string; business_detail_field_id: string; key: string; name: string; value: string }>
    extraInfos?: Array<{ id: string; business_extra_field_id: string; key: string; name: string; value: string }>
    /** AI 분석 프롬프트에 함께 실리는 판단근거 (수정 모드 전용) */
    judgmentBasis?: string
  }
}

// Section Header Component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div 
    className="pb-2 border-b" 
    style={{ 
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3 
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
  </div>
)

// Info Field Component
const InfoField: React.FC<{ 
  label: string
  value: string | React.ReactNode
  fullWidth?: boolean 
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      <span 
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}
      >
        {label}
      </span>
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span 
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '16px',
            fontWeight: 400,
            color: designTokens.colors.gray[6]
          }}
        >
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

const TaskCreatePanel: React.FC<TaskCreatePanelProps> = ({
  isOpen,
  onClose,
  taskSubgroupId,
  taskSubgroupName,
  taskGroupName,
  businessName,
  businessId,
  onSuccess,
  editMode = false,
  taskId,
  initialTaskData
}) => {
  const [taskName, setTaskName] = useState(initialTaskData?.name || '')
  // AI 분석 프롬프트에 함께 실리는 판단근거 — 워크스페이스 AI가 켜진 경우에만 노출·전송한다
  const canUseAI = useCanUseAI()
  const [judgmentBasis, setJudgmentBasis] = useState(initialTaskData?.judgmentBasis || '')
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(initialTaskData?.departmentId || null)
  const [selectedDepartmentName, setSelectedDepartmentName] = useState<string | null>(null)
  const [departmentError, setDepartmentError] = useState<string | null>(null)
  
  // Role assignment state
  const [assigneeId, setAssigneeId] = useState<string | null>(initialTaskData?.assigneeId || null)
  const [assigneeName, setAssigneeName] = useState<string | null>(null)
  
  const [subAssigneeId, setSubAssigneeId] = useState<string | null>(initialTaskData?.subAssigneeId || null)
  const [subAssigneeName, setSubAssigneeName] = useState<string | null>(null)
  
  const [managerId, setManagerId] = useState<string | null>(initialTaskData?.managerId || null)
  const [managerName, setManagerName] = useState<string | null>(null)
  
  const [reviewerId, setReviewerId] = useState<string | null>(initialTaskData?.reviewerId || null)
  const [reviewerName, setReviewerName] = useState<string | null>(null)
  
  const [budgetData, setBudgetData] = useState<{
    id: string
    description: string
    items: Array<{
      id: string
      name: string
      budget: number | null
      execution: number | null
    }>
  }>({
    id: initialTaskData?.budget?.id || '',
    description: initialTaskData?.budget?.resultDescription || '',
    items: initialTaskData?.budget?.items?.map((item, _index) => ({
      id: item.id,
      name: item.name,
      budget: typeof item.targetFigure === 'string' ? parseInt(item.targetFigure, 10) || 0 : item.targetFigure,
      execution: typeof item.doneFigure === 'string' ? parseInt(item.doneFigure, 10) || 0 : item.doneFigure
    })) || []
  })
  const [performanceData, setPerformanceData] = useState<{
    id: string
    performanceName: string
    unit: string
    items: Array<{
      id: string
      name: string
      target: number | null
      achievement: number | null
    }>
  }>({
    id: initialTaskData?.performance?.id || '',
    performanceName: initialTaskData?.performance?.name || '',
    unit: initialTaskData?.performance?.unit || '',
    items: initialTaskData?.performance?.items?.map((item, _index) => ({
      id: item.id,
      name: item.name,
      target: typeof item.targetFigure === 'string' ? parseInt(item.targetFigure, 10) || 0 : item.targetFigure,
      achievement: typeof item.doneFigure === 'string' ? parseInt(item.doneFigure, 10) || 0 : item.doneFigure
    })) || []
  })
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isMemoModalOpen, setIsMemoModalOpen] = useState(false)
  const { showError, showSuccess } = useAlert()

  // Workspace members data - centralized for all MemberSelect components
  const [workspaceMembers, setWorkspaceMembers] = useState<OrgMemberResponse[]>([])
  const [isMembersLoading, setIsMembersLoading] = useState(false)
  const [membersError, setMembersError] = useState<string | null>(null)

  // Business data for dynamic fields
  const [businessData, setBusinessData] = useState<BusinessResponse | null>(null)
  const [isBusinessLoading, setIsBusinessLoading] = useState(false)
  const [businessError, setBusinessError] = useState<string | null>(null)

  // Dynamic field states
  const [selectedLabels, setSelectedLabels] = useState<string[]>([])
  const [detailFieldsData, setDetailFieldsData] = useState<Record<string, string>>({})
  const [extraFieldsData, setExtraFieldsData] = useState<Record<string, string>>({})

  // Departments data - centralized for DepartmentSelect component
  const [departments, setDepartments] = useState<DepartmentResponse[]>([])
  const [isDepartmentsLoading, setIsDepartmentsLoading] = useState(false)
  const [departmentsError, setDepartmentsError] = useState<string | null>(null)

  // Get workspace from Redux store
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)

  // Unified reset function for all fields
  const resetAllFields = useCallback(() => {
    // Basic fields
    setTaskName('')
    setSelectedDepartmentId(null)
    setSelectedDepartmentName(null)
    setDepartmentError(null)

    // Role assignments
    setAssigneeId(null)
    setAssigneeName(null)
    setSubAssigneeId(null)
    setSubAssigneeName(null)
    setManagerId(null)
    setManagerName(null)
    setReviewerId(null)
    setReviewerName(null)

    // Budget & Performance
    setBudgetData({ id: '', description: '', items: [] })
    setPerformanceData({ id: '', performanceName: '', unit: '', items: [] })

    // Dynamic fields
    setSelectedLabels([])
    setDetailFieldsData({})
    setExtraFieldsData({})
  }, [])

  // initialTaskData가 변경될 때 상태를 업데이트
  useEffect(() => {
    if (editMode && initialTaskData) {
      setTaskName(initialTaskData.name || '')
      setSelectedDepartmentId(initialTaskData.departmentId || null)
      setSelectedDepartmentName(initialTaskData.departmentName || null)
      setAssigneeId(initialTaskData.assigneeId || null)
      setAssigneeName(initialTaskData.assigneeName || null)
      setSubAssigneeId(initialTaskData.subAssigneeId || null)
      setSubAssigneeName(initialTaskData.subAssigneeName || null)
      setManagerId(initialTaskData.managerId || null)
      setManagerName(initialTaskData.managerName || null)
      setReviewerId(initialTaskData.reviewerId || null)
      setReviewerName(initialTaskData.reviewerName || null)
      setJudgmentBasis(initialTaskData.judgmentBasis || '')

      // Dynamic fields 데이터 업데이트
      if (initialTaskData.labels) {
        // BusinessLabelInfo has 'id' field, not 'business_label_id'
        const labelIds = initialTaskData.labels.map(label => label.id)
        setSelectedLabels(labelIds)
      } else {
        setSelectedLabels([])
      }

      if (initialTaskData.detailInfos) {
        const detailFieldsMap = initialTaskData.detailInfos.reduce((acc, info) => {
          acc[info.business_detail_field_id] = info.value
          return acc
        }, {} as Record<string, string>)
        setDetailFieldsData(detailFieldsMap)
      } else {
        setDetailFieldsData({})
      }

      if (initialTaskData.extraInfos) {
        const extraFieldsMap = initialTaskData.extraInfos.reduce((acc, info) => {
          acc[info.business_extra_field_id] = info.value
          return acc
        }, {} as Record<string, string>)
        setExtraFieldsData(extraFieldsMap)
      } else {
        setExtraFieldsData({})
      }

      // Budget 데이터 업데이트
      if (initialTaskData.budget) {
        setBudgetData({
          id: initialTaskData.budget.id,
          description: initialTaskData.budget.resultDescription || '',
          items: initialTaskData.budget.items?.map((item, _index) => ({
            id: item.id,
            name: item.name,
            budget: item.targetFigure,
            execution: item.doneFigure
          })) || []
        })
      } else {
        setBudgetData({ description: '', items: [] })
      }
      
      // Performance 데이터 업데이트
      if (initialTaskData.performance) {
        setPerformanceData({
          id: initialTaskData.performance.id,
          performanceName: initialTaskData.performance.name || '',
          unit: initialTaskData.performance.unit || '',
          items: initialTaskData.performance.items?.map((item, index) => ({
            id: item.id,
            name: item.name,
            target: item.targetFigure,
            achievement: item.doneFigure
          })) || []
        })
      } else {
        setPerformanceData({ performanceName: '', unit: '', items: [] })
      }
    } else if (!editMode) {
      // 수정 모드가 아닐 때는 모든 상태를 초기화
      resetAllFields()
    }
  }, [editMode, initialTaskData, resetAllFields])

  // Fetch workspace members once when workspace changes
  useEffect(() => {
    const fetchWorkspaceMembers = async () => {
      if (!selectedWorkspace?.id) return
      
      setIsMembersLoading(true)
      setMembersError(null)
      
      try {
        const response: PaginatedResponse = await OrgMemberService.appsOrganizationViewsListOrgMembers({
          orgId: selectedWorkspace.org_id,
          workspaceId: selectedWorkspace.id,
          page: 1,
          perPage: 1000
        })
        
        // Transform OrgMemberResponse to WorkspaceMemberResponse format
        const orgMembers = response.data as OrgMemberResponse[] || []
        const workspaceMembers: OrgMemberResponse[] = orgMembers
          .filter(member => member.workspaces?.some(ws => ws.id === selectedWorkspace.id))
          .map(member => ({
            id: member.id,
            account_id: member.account_id,
            account_name: member.account_name,
            account_email: member.account_email,
            account_phone: member.account_phone,
            workspace_id: selectedWorkspace.id,
            workspace_name: member.workspaces?.find(ws => ws.id === selectedWorkspace.id)?.name || ''
          }))
        
        setWorkspaceMembers(workspaceMembers)
      } catch (err) {
        console.error('Failed to fetch workspace members:', err)
        setMembersError('멤버 목록을 불러오는데 실패했습니다.')
      } finally {
        setIsMembersLoading(false)
      }
    }
    
    fetchWorkspaceMembers()
  }, [selectedWorkspace?.id, selectedWorkspace?.org_id])

  // Fetch departments once when workspace changes
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!selectedWorkspace?.id) return
      
      setIsDepartmentsLoading(true)
      setDepartmentsError(null)
      
      try {
        const response: PaginatedResponse = await DepartmentService.appsDepartmentViewsListDepartments({
          workspaceId: selectedWorkspace.id,
          page: 1,
          perPage: 100
        })
        
        setDepartments(response.data as DepartmentResponse[] || [])
      } catch (err) {
        console.error('Failed to fetch departments:', err)
        setDepartmentsError('부서 목록을 불러오는데 실패했습니다.')
      } finally {
        setIsDepartmentsLoading(false)
      }
    }
    
    fetchDepartments()
  }, [selectedWorkspace?.id])

  // Fetch business data when businessId changes (for dynamic fields)
  useEffect(() => {
    const fetchBusinessData = async () => {
      if (!businessId || !selectedWorkspace?.id) {
        return
      }

      setIsBusinessLoading(true)
      setBusinessError(null)

      try {
        const response = await BusinessService.appsBusinessViewsGetBusiness({
          businessId: businessId
        })

        setBusinessData(response)
      } catch (err) {
        console.error('Failed to fetch business data:', err)
        setBusinessError('사업 정보를 불러오는데 실패했습니다.')
      } finally {
        setIsBusinessLoading(false)
      }
    }

    fetchBusinessData()
  }, [businessId, selectedWorkspace?.id])

  // Note: Label synchronization is handled in the main useEffect (lines 250-341)
  // This dedicated useEffect was causing duplicate updates and has been removed

  // Department selection handler
  const handleDepartmentSelect = (departmentId: string, departmentName: string) => {
    setSelectedDepartmentId(departmentId)
    setSelectedDepartmentName(departmentName)
    setDepartmentError(null)
  }

  // Role assignment handlers
  const handleAssigneeSelect = (memberId: string, memberName: string, _memberEmail: string) => {
    setAssigneeId(memberId || null)
    setAssigneeName(memberName || null)
  }

  const handleSubAssigneeSelect = (memberId: string, memberName: string, _memberEmail: string) => {
    setSubAssigneeId(memberId || null)
    setSubAssigneeName(memberName || null)
  }

  const handleManagerSelect = (memberId: string, memberName: string, _memberEmail: string) => {
    setManagerId(memberId || null)
    setManagerName(memberName || null)
  }

  const handleReviewerSelect = (memberId: string, memberName: string, _memberEmail: string) => {
    setReviewerId(memberId || null)
    setReviewerName(memberName || null)
  }

  const handleCancel = () => {
    resetAllFields()
    onClose()
  }

  const handleSave = async () => {
    // Validation
    if (!taskName.trim()) {
      showError('세부실행과제명을 입력해주세요.')
      return
    }
    if (!selectedDepartmentId) {
      setDepartmentError('부서를 선택해주세요.')
      return
    }
    if (!taskSubgroupId) {
      showError('실행과제가 선택되지 않았습니다.')
      return
    }
    if (!selectedWorkspace) {
      showError('워크스페이스가 선택되지 않았습니다.')
      return
    }
    if (editMode && !taskId) {
      showError('수정할 세부실행과제 ID가 없습니다.')
      return
    }

    // 수정 모드: 메모 입력 모달을 띄운 뒤 저장
    if (editMode) {
      setIsMemoModalOpen(true)
      return
    }

    await performSave()
  }

  // 실제 생성/수정 수행. 수정 모드에서는 메모 모달 확인 시 memo와 함께 호출된다.
  const performSave = async (memo?: string) => {
    setIsSubmitting(true)
    setDepartmentError(null)

    try {
      let resultId: string | undefined

      if (editMode) {
        // Edit Mode: Update existing task
        resultId = taskId

        // Step 1: Update Task basic info
        // Filter out empty label IDs
        const selectedLabelIds = selectedLabels.filter(labelId => labelId.trim() !== '')
        
        // Prepare detail_infos and extra_infos
        const nonEmptyDetailFields = Object.entries(detailFieldsData)
          .filter(([_, value]) => value.trim() !== '')
        const nonEmptyExtraFields = Object.entries(extraFieldsData)
          .filter(([_, value]) => value.trim() !== '')

        const taskUpdateRequest: TaskUpdateRequest = {
          name: taskName.trim(),
          origin_workspace_id: selectedWorkspace.id,
          department_id: selectedDepartmentId,
          assignee_id: assigneeId || null,
          sub_assignee_id: subAssigneeId || null,
          manager_id: managerId || null,
          reviewer_id: reviewerId || null,
          labels: selectedLabelIds.length > 0 ? selectedLabelIds : null,
          detail_infos: nonEmptyDetailFields.length > 0 ? nonEmptyDetailFields.map(([fieldId, value]) => ({
            business_detail_field_id: fieldId,
            value: value
          })) : null,
          extra_infos: nonEmptyExtraFields.length > 0 ? nonEmptyExtraFields.map(([fieldId, value]) => ({
            business_extra_field_id: fieldId,
            value: value
          })) : null,
          // AI 미사용 워크스페이스에서는 키 자체를 보내지 않는다 (키 생략 = 기존 값 유지).
          // 켜진 경우 빈 값이면 null을 보내 판단근거를 삭제한다.
          ...(canUseAI ? { judgment_basis: judgmentBasis.trim() || null } : {}),
          memo
        }

        await TaskService.appsTaskViewsUpdateTask({
          taskId: taskId!,
          requestBody: taskUpdateRequest
        })

        // Step 3: Handle Budget Update/Creation/Deletion
        // 폼을 비운 채 저장 = 예산 삭제. (예산 자체를 지우는 버튼이 없어 이 경로가 유일한 삭제 수단이다)
        const budgetIsEmpty = !budgetData.description.trim() && budgetData.items.length === 0
        if (budgetIsEmpty) {
          if (budgetData.id) {
            await TaskBudgetService.appsTaskViewsDeleteTaskBudget({ budgetId: budgetData.id })
          }
        } else {
          // 폼에 id 가 없어도 서버에 이미 예산이 있으면 그것을 갱신한다.
          // (초기 데이터에 예산이 실리지 않은 상태에서 저장하면 같은 값의 예산이 중복 생성되던 문제)
          let budgetId = budgetData.id || null
          if (!budgetId && selectedWorkspace?.id) {
            const existing = await TaskBudgetService.appsTaskViewsListTaskBudgets({
              workspaceId: selectedWorkspace.id,
              taskId: taskId!,
              page: 1,
              perPage: 1
            })
            budgetId = existing?.data?.[0]?.id ?? null
          }

          if (budgetId) {
            // Update existing budget
            const budgetUpdateRequest: TaskBudgetUpdateRequest = {
              result_description: budgetData.description.trim() || null
            }

            await TaskBudgetService.appsTaskViewsUpdateTaskBudget({
              budgetId,
              requestBody: budgetUpdateRequest
            })

            // Step 4: Bulk update budget items
            const budgetItemsRequest: TaskBudgetItemsBulkUpdateRequest = {
              items: budgetData.items.map(item => ({
                id: item.id && !item.id.startsWith('new-budget-') ? item.id : null, // Filter out temporary IDs
                name: item.name,
                target_figure: item.budget || 0,
                done_figure: item.execution || 0
              }))
            }

            await TaskBudgetItemService.appsTaskViewsUpdateTaskBudgetItemsBulk({
              budgetId,
              requestBody: budgetItemsRequest
            })
          } else {
            // Create new budget
            const budgetCreateRequest: TaskBudgetCreateRequest = {
              task_id: taskId!,
              result_description: budgetData.description.trim(),
              items: budgetData.items.length > 0 ? budgetData.items.map(item => ({
                name: item.name,
                target_figure: item.budget || 0,
                done_figure: item.execution || 0
              })) : undefined
            }

            await TaskBudgetService.appsTaskViewsCreateTaskBudget({
              requestBody: budgetCreateRequest
            })
          }
        }

        // Step 5: Handle Performance Update/Creation/Deletion
        // 폼을 비운 채 저장 = 성과 삭제. 백엔드 delete 가 성과 항목까지 함께 소프트 삭제한다.
        const performanceIsEmpty = !performanceData.performanceName.trim() && performanceData.items.length === 0
        if (performanceIsEmpty) {
          if (performanceData.id) {
            await TaskPerformanceService.appsTaskViewsDeleteTaskPerformance({ taskPerformanceId: performanceData.id })
          }
        } else {
          // 폼에 id 가 없어도 서버에 이미 성과가 있으면 그것을 갱신한다 (중복 생성 방지, 예산과 같은 이유)
          let performanceId = performanceData.id || null
          if (!performanceId && selectedWorkspace?.id) {
            const existing = await TaskPerformanceService.appsTaskViewsListTaskPerformances({
              workspaceId: selectedWorkspace.id,
              taskId: taskId!,
              page: 1,
              perPage: 1
            })
            performanceId = existing?.data?.[0]?.id ?? null
          }

          if (performanceId) {
            // Update existing performance
            const performanceUpdateRequest: TaskPerformanceUpdateRequest = {
              name: performanceData.performanceName.trim() || null,
              unit: performanceData.unit.trim() || null
            }

            await TaskPerformanceService.appsTaskViewsUpdateTaskPerformance({
              taskPerformanceId: performanceId,
              requestBody: performanceUpdateRequest
            })

            // Step 6: Bulk update performance items
            const performanceItemsRequest: TaskPerformanceItemsBulkUpdateRequest = {
              items: performanceData.items.map(item => ({
                id: item.id && !item.id.startsWith('new-performance-') ? item.id : null, // Filter out temporary IDs
                name: item.name,
                target_figure: item.target || 0,
                done_figure: item.achievement || 0
              }))
            }

            await TaskPerformanceItemService.appsTaskViewsUpdateTaskPerformanceItemsBulk({
              performanceId,
              requestBody: performanceItemsRequest
            })
          } else {
            // Create new performance
            const performanceCreateRequest: TaskPerformanceCreateRequest = {
              task_id: taskId!,
              name: performanceData.performanceName.trim(),
              unit: performanceData.unit.trim(),
              items: performanceData.items.length > 0 ? performanceData.items.map(item => ({
                name: item.name,
                target_figure: item.target || 0,
                done_figure: item.achievement || 0
              })) : undefined
            }

            await TaskPerformanceService.appsTaskViewsCreateTaskPerformance({
              requestBody: performanceCreateRequest
            })
          }
        }

      } else {
        // Create Mode: Create new task (existing logic)

        // Prepare dynamic fields data (only include non-empty values)
        const selectedLabelIds = selectedLabels.filter(labelId => labelId.trim() !== '')
        const nonEmptyDetailFields = Object.entries(detailFieldsData)
          .filter(([_, value]) => value.trim() !== '')
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
        const nonEmptyExtraFields = Object.entries(extraFieldsData)
          .filter(([_, value]) => value.trim() !== '')
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})

        const taskFullRequest: TaskFullCreateRequest = {
          task_subgroup_id: taskSubgroupId,
          name: taskName.trim(),
          origin_workspace_id: selectedWorkspace.id,
          department_id: selectedDepartmentId,
          assignee_id: assigneeId || '',
          sub_assignee_id: subAssigneeId || '',
          manager_id: managerId || '',
          reviewer_id: reviewerId || '',
          // Add dynamic fields only if they have values
          ...(selectedLabelIds.length > 0 && { labels: selectedLabelIds }),
          ...(Object.keys(nonEmptyDetailFields).length > 0 && {
            detail_infos: Object.entries(nonEmptyDetailFields).map(([fieldId, value]) => ({
              business_detail_field_id: fieldId,
              value: value
            }))
          }),
          ...(Object.keys(nonEmptyExtraFields).length > 0 && {
            extra_infos: Object.entries(nonEmptyExtraFields).map(([fieldId, value]) => ({
              business_extra_field_id: fieldId,
              value: value
            }))
          })
        }

        const taskResponse = await TaskService.appsTaskViewsCreateTaskFull({
          requestBody: taskFullRequest
        })
        const createdTaskId = taskResponse.id
        resultId = createdTaskId

        // Create Budget if budget data exists
        if (budgetData.description.trim() || budgetData.items.length > 0) {
          const budgetRequest: TaskBudgetCreateRequest = {
            task_id: createdTaskId,
            result_description: budgetData.description.trim(),
            items: budgetData.items.length > 0 ? budgetData.items.map(item => ({
              name: item.name,
              target_figure: item.budget || 0,
              done_figure: item.execution || 0
            })) : undefined
          }

          await TaskBudgetService.appsTaskViewsCreateTaskBudget({
            requestBody: budgetRequest
          })
        }

        // Create Performance if performance data exists
        if (performanceData.performanceName.trim() || performanceData.items.length > 0) {
          const performanceRequest: TaskPerformanceCreateRequest = {
            task_id: createdTaskId,
            name: performanceData.performanceName.trim(),
            unit: performanceData.unit.trim(),
            items: performanceData.items.length > 0 ? performanceData.items.map(item => ({
              name: item.name,
              target_figure: item.target || 0,
              done_figure: item.achievement || 0
            })) : undefined
          }

          await TaskPerformanceService.appsTaskViewsCreateTaskPerformance({
            requestBody: performanceRequest
          })
        }
      }

      // Success - show message and call onSuccess first before closing
      showSuccess(`세부실행과제가 ${editMode ? '수정' : '생성'}되었습니다.`)

      // Call onSuccess before closing so parent can access state
      // Wait for onSuccess to complete if it's async (especially for edit mode)
      if (onSuccess) {
        await onSuccess(resultId)
      }

      // Reset form (only in create mode) and close panel
      if (!editMode) {
        resetAllFields()
      }
      setIsMemoModalOpen(false)
      onClose()

    } catch (err) {
      console.error('API error:', err)

      // Use common error handler
      const errorPrefix = editMode
        ? ErrorMessages.TASK_UPDATE_FAILED
        : ErrorMessages.TASK_CREATE_FAILED
      handleApiError(err, showError, errorPrefix)
    } finally {
      setIsSubmitting(false)
    }
  }

  const headerActions = (
    <>
      <Button
        size="small"
        color="white"
        onClick={handleCancel}
        disabled={isSubmitting}
        className="flex items-center gap-1"
      >
        취소
      </Button>
      <Button
        size="small"
        color="dark"
        onClick={handleSave}
        disabled={isSubmitting || !taskName.trim() || !selectedDepartmentId}
        className="flex items-center gap-1"
      >
        {isSubmitting ? '저장 중...' : '저장'}
      </Button>
    </>
  )

  return (
    <>
    <SidePanel
      isOpen={isOpen}
      onClose={handleCancel}
      title={editMode ? "세부실행과제 수정" : "세부실행과제 생성"}
      width={800}
      headerActions={headerActions}
    >
      <div 
        className="flex flex-col space-y-2" 
        style={{ 
          padding: '24px',
        }}
      >
        {/* Section: 기본정보 */}
        <div className="space-y-2">
          <SectionHeader title="기본정보" />
          <div className="space-y-2" style={{ padding: '0px 4px' }}>
            <InfoField 
              label="유형" 
              value={
                <Tag color="success" size="small">
                  ● 세부실행과제
                </Tag>
              }
            />
            <InfoField 
              label="계층" 
              value={`${businessName} > ${taskGroupName} > ${taskSubgroupName}`}
            />
            <InfoField
              label={<RequiredLabel>세부실행과제명</RequiredLabel>}
              value={
                <input
                  type="text"
                  value={taskName}
                  onChange={(e) => setTaskName(e.target.value)}
                  placeholder="세부실행과제명을 입력하세요"
                  disabled={isSubmitting}
                  className="w-full p-3 border rounded"
                  style={{
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    color: designTokens.colors.gray[6]
                  }}
                />
              }
              fullWidth
            />
            <InfoField
              label={<RequiredLabel>부서</RequiredLabel>}
              value={
                <DepartmentSelect
                  selectedDepartmentId={selectedDepartmentId}
                  selectedDepartmentName={selectedDepartmentName}
                  onSelect={handleDepartmentSelect}
                  disabled={isSubmitting}
                  error={departmentError}
                  placeholder="부서를 선택하세요"
                  workspaceId={selectedWorkspace?.id || ''}
                  departments={departments}
                  isLoading={isDepartmentsLoading}
                  departmentsError={departmentsError}
                />
              }
              fullWidth
            />
            
            <InfoField
              label={<RequiredLabel>담당자</RequiredLabel>}
              value={
                <SearchableMemberSelect
                  selectedMemberId={assigneeId}
                  selectedMemberName={assigneeName}
                  onSelect={handleAssigneeSelect}
                  disabled={isSubmitting}
                  placeholder="담당자를 선택하세요"
                  searchPlaceholder="담당자 이름으로 검색..."
                  workspaceId={selectedWorkspace?.id || ''}
                  members={workspaceMembers}
                  isLoading={isMembersLoading}
                  membersError={membersError}
                />
              }
              fullWidth
            />

            <InfoField
              label="부담당자"
              value={
                <SearchableMemberSelect
                  selectedMemberId={subAssigneeId}
                  selectedMemberName={subAssigneeName}
                  onSelect={handleSubAssigneeSelect}
                  disabled={isSubmitting}
                  placeholder="부담당자를 선택하세요"
                  searchPlaceholder="부담당자 이름으로 검색..."
                  workspaceId={selectedWorkspace?.id || ''}
                  members={workspaceMembers}
                  isLoading={isMembersLoading}
                  membersError={membersError}
                />
              }
              fullWidth
            />

            <InfoField
              label={<RequiredLabel>관리자</RequiredLabel>}
              value={
                <SearchableMemberSelect
                  selectedMemberId={managerId}
                  selectedMemberName={managerName}
                  onSelect={handleManagerSelect}
                  disabled={isSubmitting}
                  placeholder="관리자를 선택하세요"
                  searchPlaceholder="관리자 이름으로 검색..."
                  workspaceId={selectedWorkspace?.id || ''}
                  members={workspaceMembers}
                  isLoading={isMembersLoading}
                  membersError={membersError}
                />
              }
              fullWidth
            />

            <InfoField
              label={<RequiredLabel>검토자</RequiredLabel>}
              value={
                <SearchableMemberSelect
                  selectedMemberId={reviewerId}
                  selectedMemberName={reviewerName}
                  onSelect={handleReviewerSelect}
                  disabled={isSubmitting}
                  placeholder="검토자를 선택하세요"
                  searchPlaceholder="검토자 이름으로 검색..."
                  workspaceId={selectedWorkspace?.id || ''}
                  members={workspaceMembers}
                  isLoading={isMembersLoading}
                  membersError={membersError}
                />
              }
              fullWidth
            />
            {/* 판단근거 — AI 분석 프롬프트에 함께 실린다.
                워크스페이스 AI가 켜져 있고, 생성 API는 미지원이라 수정 모드일 때만 노출 */}
            {editMode && canUseAI && (
              <InfoField
                label="판단근거"
                value={
                  <textarea
                    value={judgmentBasis}
                    onChange={(e) => setJudgmentBasis(e.target.value)}
                    placeholder="AI 분석 시 함께 전달할 판단근거를 입력하세요"
                    disabled={isSubmitting}
                    rows={6}
                    className="w-full p-3 border rounded resize-y"
                    style={{
                      borderColor: designTokens.colors.gray[1],
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      color: designTokens.colors.gray[6]
                    }}
                  />
                }
                fullWidth
              />
            )}
          </div>
        </div>

        {/* Section: 라벨 */}
        {businessId && (
          <div className="space-y-2">
            <SectionHeader title="라벨" />
            <div className="space-y-2" style={{ padding: '0px 4px' }}>
              <BusinessLabelSelector
                labels={businessData?.labels || []}
                selectedLabels={selectedLabels}
                onLabelsChange={setSelectedLabels}
                isLoading={isBusinessLoading}
                error={businessError}
              />
            </div>
          </div>
        )}

        {/* Section: 예산 */}
        <div className="space-y-2">
          <BudgetSection
            budgetData={budgetData}
            onChange={setBudgetData}
            disabled={isSubmitting}
          />
        </div>

        {/* Section: 성과 */}
        <div className="space-y-2">
          <PerformanceSection
            performanceData={performanceData}
            onChange={setPerformanceData}
            disabled={isSubmitting}
          />
        </div>

        {/* Section: 상세 정보 */}
        {businessId && (
          <div className="space-y-2">
            <SectionHeader title="상세 정보" />
            <div className="space-y-2" style={{ padding: '0px 4px' }}>
              <BusinessDetailFields
                detailFields={businessData?.detail_fields || []}
                fieldValues={detailFieldsData}
                onFieldChange={(fieldId, value) => setDetailFieldsData(prev => ({ ...prev, [fieldId]: value }))}
                isLoading={isBusinessLoading}
                error={businessError}
              />
            </div>
          </div>
        )}

        {/* Section: 추가 정보 */}
        {businessId && (
          <div className="space-y-2">
            <SectionHeader title="추가 정보" />
            <div className="space-y-2" style={{ padding: '0px 4px' }}>
              <BusinessExtraFields
                extraFields={businessData?.extra_fields || []}
                fieldValues={extraFieldsData}
                onFieldChange={(fieldId, value) => setExtraFieldsData(prev => ({ ...prev, [fieldId]: value }))}
                isLoading={isBusinessLoading}
                error={businessError}
              />
            </div>
          </div>
        )}
      </div>
    </SidePanel>

    <MemoModal
      isOpen={isMemoModalOpen}
      onClose={() => setIsMemoModalOpen(false)}
      onConfirm={performSave}
      title="세부실행과제 수정"
      isSubmitting={isSubmitting}
    />
    </>
  )
}

export default TaskCreatePanel